import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useSession } from "@/lib/auth-client";
import { authClient } from "@/lib/auth-client";
import { api } from "@/api";

interface UsageInfo {
  events_this_month: number;
  events_limit: number;
}

interface LimitsInfo {
  max_sources: number;
  max_destinations: number;
  retention_days: number;
}

interface OrgInfo {
  id: string;
  name: string;
  slug: string | null;
  logo: string | null;
}

export interface EnvironmentInfo {
  id: string;
  org_id: string;
  name: string;
  slug: string;
  created_at: string;
}

interface OrgContextValue {
  orgId: string;
  envId: string;
  envName: string;
  envSlug: string;
  loading: boolean;
  plan: string;
  usage: UsageInfo | null;
  limits: LimitsInfo | null;
  orgs: OrgInfo[];
  currentOrg: OrgInfo | null;
  environments: EnvironmentInfo[];
  switchOrg: (orgId: string) => Promise<void>;
  switchEnv: (envId: string) => Promise<void>;
  refetchMe: () => Promise<void>;
  refetchOrgs: () => Promise<void>;
  refetchEnvironments: () => Promise<void>;
}

const OrgContext = createContext<OrgContextValue>({
  orgId: "",
  envId: "",
  envName: "Production",
  envSlug: "production",
  loading: true,
  plan: "free",
  usage: null,
  limits: null,
  orgs: [],
  currentOrg: null,
  environments: [],
  switchOrg: async () => {},
  switchEnv: async () => {},
  refetchMe: async () => {},
  refetchOrgs: async () => {},
  refetchEnvironments: async () => {},
});

export function OrgProvider({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const [orgId, setOrgId] = useState("");
  const [envId, setEnvId] = useState("");
  const [envName, setEnvName] = useState("Production");
  const [envSlug, setEnvSlug] = useState("production");
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState("free");
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [limits, setLimits] = useState<LimitsInfo | null>(null);
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const [currentOrg, setCurrentOrg] = useState<OrgInfo | null>(null);
  const [environments, setEnvironments] = useState<EnvironmentInfo[]>([]);

  const fetchOrgs = useCallback(async () => {
    try {
      const { data } = await authClient.organization.list();
      if (data) {
        setOrgs(data.map((o) => ({
          id: o.id,
          name: o.name,
          slug: o.slug || null,
          logo: o.logo ?? null,
        })));
      }
    } catch {
      // Non-critical
    }
  }, []);

  const fetchEnvironments = useCallback(async () => {
    try {
      const data = await api<{ environments: EnvironmentInfo[] }>("/environments");
      setEnvironments(data.environments);
    } catch {
      // Non-critical
    }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const data = await api<{
        org_id: string;
        env_id: string;
        env_name?: string;
        env_slug?: string;
        plan?: string;
        usage?: UsageInfo;
        limits?: LimitsInfo;
      }>("/me");
      setOrgId(data.org_id);
      setEnvId(data.env_id);
      if (data.env_name) setEnvName(data.env_name);
      if (data.env_slug) setEnvSlug(data.env_slug);
      if (data.plan) setPlan(data.plan);
      if (data.usage) setUsage(data.usage);
      if (data.limits) setLimits(data.limits);

      // Update current org info
      setCurrentOrg(orgs.find(o => o.id === data.org_id) || null);

      // Auto-claim test session if present (graduation)
      const testSessionId = localStorage.getItem("hookstream_test_session_id");
      if (testSessionId) {
        try {
          const res = await fetch(`/v1/test/sessions/${testSessionId}/claim`, {
            method: "POST",
            credentials: "include",
          });
          if (res.ok || res.status === 404 || res.status === 410) {
            localStorage.removeItem("hookstream_test_session_id");
            localStorage.removeItem("hookstream_test_session_expires");
          }
        } catch {
          // Non-critical
        }
      }
    } catch {
      // Will retry on next render
    } finally {
      setLoading(false);
    }
  }, [orgs]);

  const switchOrg = useCallback(async (newOrgId: string) => {
    try {
      await authClient.organization.setActive({ organizationId: newOrgId });
      setLoading(true);
      await fetchMe();
      await fetchEnvironments();
    } catch {
      // Failed to switch
    }
  }, [fetchMe, fetchEnvironments]);

  const switchEnv = useCallback(async (newEnvId: string) => {
    try {
      await api("/environments/" + newEnvId + "/activate", { method: "POST" });
      setLoading(true);
      await fetchMe();
    } catch {
      // Failed to switch
    }
  }, [fetchMe]);

  useEffect(() => {
    if (session) {
      fetchOrgs();
    }
  }, [session, fetchOrgs]);

  useEffect(() => {
    if (session && orgs.length >= 0) {
      fetchMe();
      fetchEnvironments();
    }
  }, [session, orgs, fetchMe, fetchEnvironments]);

  return (
    <OrgContext.Provider value={{
      orgId, envId, envName, envSlug, loading, plan, usage, limits,
      orgs, currentOrg, environments, switchOrg, switchEnv,
      refetchMe: fetchMe, refetchOrgs: fetchOrgs, refetchEnvironments: fetchEnvironments,
    }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg(): OrgContextValue {
  return useContext(OrgContext);
}
